/**
 *
 * @module oneTimePasscode
 * @desc oneTimePasscode sequence implementation
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/specs/feature_overviews/identity-bam.md#one-time-passcodes
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../../sdk';
import BaseSequence from './../../base';
import Exception from './../../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    nonEmptyObject,
    boolean
} = Check.assert;

/**
 *
 * @class OneTimePasscodeSequence
 * @extends {BaseSequence}
 *
 * @example const oneTimePasscodeSequence = new SDK.Test.Sequences.OneTimePasscode(config, options);
 *
 */
export default class OneTimePasscodeSequence extends BaseSequence {

    /**
     *
     * @param {Object} config configuration used to build a BootstrapConfiguration instance
     * @param {Object} options contains one time password params
     * @param {String} options.email the email to login
     * @param {Boolean} [clearOnComplete=false] flag that determines if token refresh
     * should be cleared when the sequence is complete (useful when running tests)
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, options, clearOnComplete = false) {

        const errorMessage = {
            ctor: 'OneTimePasscodeSequence(config, options, clearOnComplete)',
            get config() {
                return `${this.ctor} config should be a non-empty Object`;
            },
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get clearOnComplete() {
                return `${this.ctor} clearOnComplete should be a Boolean`;
            }
        };

        nonEmptyObject(config, errorMessage.config, InvalidArgumentException);
        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);
        boolean(clearOnComplete, errorMessage.clearOnComplete, InvalidArgumentException);

        super(config);

        /**
         *
         * @access public
         * @type {Object}
         *
         */
        this.options = options;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.clearOnComplete = clearOnComplete;

    }

    /**
     *
     * @param {SDK.SdkSession} session
     * @param {String} endpoint the name of the endpoint to add the mock and auth headers for
     * @note The Authorization header was created using the instructions from
     * https://wiki.bamtechmedia.com/display/DEVOPS/Multiple+Profiles+Mocks
     * with 8ca3290d-bc08-4cc2-98f8-c59009b347a0 as the account id
     *
     */
    addMockHeaders(session, endpoint) {

        const endpoints = SDK.Services.Util.getSafe(() => session.accountManager.client.config.endpoints);

        endpoints[endpoint].headers.Authorization = 'Bearer eyJraWQiOiJlNzliMTEyYS1jZmExLTExZTctYWJjNC1jZWMyNzhiNmI1MGEiLCJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiI3YTEzNGVlMy0xMTQyLTQ3MGUtYWM3Yi1lOGUzNDIwZDEzMTQiLCJuYmYiOjE1NDMzNjYyNTksInBhcnRuZXJOYW1lIjoiZXNwbiIsImlzcyI6InVybjpiYW10ZWNoOnNlcnZpY2U6dG9rZW4iLCJjb250ZXh0Ijp7InZlcnNpb24iOiJWMi4wLjAiLCJpZCI6ImFjNDk1ZjMwLWYyYTctMTFlOC1iMGIwLTAyNDJhYzExMDAwOSIsInBhcnRuZXIiOnsibmFtZSI6ImVzcG4ifSwidHlwZSI6IlJFR0lTVEVSRUQiLCJpcF9hZGRyZXNzIjoiMTM5LjEwNC4xOTIuMTAiLCJkZXZpY2UiOnsiaWQiOiI4ZDE3NzA0Mi0yMmQyLTQxMWQtOTkwMS1hMDY5YWJiYjI0MzAiLCJwbGF0Zm9ybSI6ImFwcGxlIn0sImxvY2F0aW9uIjp7InR5cGUiOiJaSVBfQ09ERSIsInppcF9jb2RlIjoiMTAxMCIsImNvdW50cnlfY29kZSI6Im5sIn0sInByb2ZpbGVzIjpbeyJhY3RpdmUiOnRydWUsInR5cGUiOiJ1cm46YmFtdGVjaDpwcm9maWxlIiwiaWQiOiI0NjYxOGEyNS0wOWQ1LTRhYmQtYWQ4My0wNWVkNGIzNjI3ZTYiLCJwYXJlbnRhbF9jb250cm9scyI6eyJlbmFibGVkIjpmYWxzZX19LHsiYWN0aXZlIjpmYWxzZSwidHlwZSI6InVybjpiYW10ZWNoOnByb2ZpbGUiLCJpZCI6IjdjNzI3MDk1LTA3OTEtNDI0YS05YmQ2LTM3OWI2YjI1NzIxYiIsInBhcmVudGFsX2NvbnRyb2xzIjp7ImVuYWJsZWQiOmZhbHNlfX1dLCJnZW5lcmF0ZWRfb24iOiIyMDE4LTExLTI4VDAwOjUwOjU5Ljg3NSswMDAwIiwidXBkYXRlZF9vbiI6IjIwMTgtMTEtMjhUMDA6NTA6NTkuODc1KzAwMDAiLCJleHBpcmVzX29uIjoiMjAxOC0xMS0yOFQwMTowMDo1OS44NzUrMDAwMCIsIm1lZGlhX3Blcm1pc3Npb25zIjp7ImVudGl0bGVtZW50cyI6WyJFU1BOX1BMVVMiXSwicnVsZXMiOnsicGFzc2VkIjpbXX0sImRhdGEiOnt9fSwiYmxhY2tvdXRzIjp7InJ1bGVzIjp7InZpb2xhdGVkIjpbXX0sImRhdGEiOnt9fSwiYWNjb3VudCI6eyJ0eXBlIjoidXJuOmJhbXRlY2g6YWNjb3VudCIsImlkIjoiOGNhMzI5MGQtYmMwOC00Y2MyLTk4ZjgtYzU5MDA5YjM0N2EwIiwiaG9tZV9sb2NhdGlvbiI6eyJ0eXBlIjoiVU5LTk9XTiJ9LCJkYXRhIjp7fX0sImxvZ2luX2NvbnRleHQiOnsiaWRwIjoiaHR0cHM6Ly9zdGcuYXV0aG9yaXphdGlvbi5nby5jb20iLCJhaWQiOiJ7QkVGMTQ3OTgtNzZCMS00QzM4LTkxOEUtQjQyMDdCMUIzMzZBfSJ9fSwic2Vzc2lvbklkIjoiYWM0OTVmMzAtZjJhNy0xMWU4LWIwYjAtMDI0MmFjMTEwMDA5IiwiZXhwIjoxNTQzMzY2ODU5LCJlbnYiOiJxYSIsImlhdCI6MTU0MzM2NjI1OSwianRpIjoiYWM0OTg2NDAtZjJhNy0xMWU4LWIwYjAtMDI0MmFjMTEwMDA5In0.5Dgxx8IZ_NDPpORisYvLI5pkFvCVP8FAs1SwzgvD2i8'; // eslint-disable-line
        endpoints[endpoint].headers['Mock-Magic-Value'] = 'persisted';

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     *
     * @example userProfileSequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, options } = this;
        const { clientId, clientApiKey, environment, debugEnabled } = config;
        const { email = 'bam@disneystreaming.com' } = options;

        const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;
        const OneTimePasscodeRequestReason = SDK.Services.Identity.Bam.OneTimePasscodeRequestReason;
        const AuthenticationFlow = SDK.Services.Identity.Bam.AuthenticationFlow;
        const RedeemedPasscodeToken = SDK.Services.Identity.Bam.RedeemedPasscodeToken;

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration({
                clientId,
                clientApiKey,
                environment,
                debugEnabled
            });

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.Identity.Bam.BamIdentityApi} */
            let bamIdentityApi;

            /** @type {SDK.Account.AccountApi} */
            let accountApi;

            /** @type {SDK.Services.Identity.Bam.AuthenticationFlow[]} */
            let authenticationFlows;

            SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((_sdkSession) => {

                /** @type {SDK.SdkSession} sa */
                sdkSession = _sdkSession;

                /** @test */
                assert.ok(_sdkSession);

                /** Establish Anonymous Session */
                return sdkSession.initialize();

            }).then(() => {

                /** @type {SDK.Identity.Bam.BamIdentityApi} */
                bamIdentityApi = sdkSession.bamIdentityApi;

                return bamIdentityApi.getAuthenticationFlow(email);

            }).then((flows) => {

                authenticationFlows = flows;

                if (authenticationFlows.includes(AuthenticationFlow.oneTimePasscode)) {
                    return bamIdentityApi.requestOneTimePasscode(email, OneTimePasscodeRequestReason.forgotPassword);
                }

                return reject();

            }).then(() => {

                const passcode = '0000'; // value used in mock

                return bamIdentityApi.authenticateWithPasscode(email, passcode);

            }).then((redeemedPasscodeToken) => {

                /** @test */
                assert.ok(redeemedPasscodeToken instanceof RedeemedPasscodeToken);

                accountApi = sdkSession.accountApi;

                return accountApi.authorize(redeemedPasscodeToken.token, 'sdk-team');

            }).then(() => {

                if (sdkSession.tokenManager) {
                    sdkSession.tokenManager.clearRetryRefresh();
                }

                return resolve();

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/identity/bam/oneTimePasscode.js